import { useState } from "react";
import { useTranslation } from "react-i18next";
import { IconPackage, IconGripVertical, IconPencil } from "@tabler/icons-react";
import { RenameModal } from "./RenameModal";
import type { Slot } from "./warehouse-types";

interface SlotTemplatePanelProps {
  onStartPlacing: (clientX: number, clientY: number) => void;
  zoneName: string;
  onRename: (name: string) => void;
  slots: Slot[];
  selectedSlotId: string | null;
  onSelectSlot: (slotId: string) => void;
}

export function SlotTemplatePanel({
  onStartPlacing,
  zoneName,
  onRename,
  slots,
  selectedSlotId,
  onSelectSlot,
}: SlotTemplatePanelProps) {
  const { t } = useTranslation();
  const [renaming, setRenaming] = useState(false);

  const handleMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
    e.preventDefault();
    onStartPlacing(e.clientX, e.clientY);
  };

  return (
    <div className="flex flex-1 flex-col">
      {/* Header */}
      <div className="border-b border-[#30333c] px-4 py-3">
        <button
          type="button"
          onClick={() => setRenaming(true)}
          title={t("warehouseMap.slotTemplate.clickToRenameZone")}
          className="group flex max-w-full items-center gap-1.5 text-lg font-semibold text-zinc-100"
        >
          <span className="truncate">{t("warehouseMap.zoneLabel", { name: zoneName })}</span>
          <IconPencil className="h-3.5 w-3.5 flex-shrink-0 text-zinc-500 opacity-0 transition-opacity group-hover:opacity-100 [@media(hover:none)]:opacity-100" />
        </button>
        <p className="text-sm text-zinc-400">{t("warehouseMap.slotTemplate.subtitle")}</p>
      </div>

      {/* Slot template */}
      <div className="p-4">
        <p className="mb-3 text-xs font-medium text-zinc-400">{t("warehouseMap.slotTemplate.templates")}</p>

        <div
          onPointerDown={handleMouseDown}
          className="group flex cursor-grab touch-none select-none items-center gap-3 rounded-lg border-2 border-dashed border-blue-400/50 bg-blue-400/10 p-4 transition-all hover:border-blue-400 hover:bg-blue-400/20 active:cursor-grabbing"
        >
          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-blue-500/20">
            <IconPackage className="h-5 w-5 text-blue-400" />
          </div>
          <div className="flex-1">
            <div className="font-medium text-zinc-100">{t("warehouseMap.newSlot")}</div>
            <div className="text-xs text-zinc-400">{t("warehouseMap.slotTemplate.dragIntoZone")}</div>
          </div>
          <IconGripVertical className="h-5 w-5 text-zinc-400 transition-colors group-hover:text-zinc-100" />
        </div>
      </div>

      {/* Existing storage locations in this area */}
      <div className="flex min-h-0 flex-1 flex-col border-t border-[#30333c] px-4 py-3">
        <p className="mb-2 flex-shrink-0 text-xs font-medium text-zinc-400">
          {t("warehouseMap.slotTemplate.slotsInZone", { count: slots.length })}
        </p>
        {slots.length === 0 ? (
          <p className="text-xs text-zinc-500">{t("warehouseMap.slotTemplate.noSlots")}</p>
        ) : (
          <div className="flex flex-col gap-1.5 overflow-y-auto">
            {slots.map((slot) => (
              <button
                key={slot.id}
                type="button"
                onClick={() => onSelectSlot(slot.id)}
                className={`flex items-center gap-2 rounded-md border px-3 py-2 text-left transition-colors ${
                  selectedSlotId === slot.id
                    ? "border-blue-400 bg-blue-400/15"
                    : "border-[#30333c] bg-[#2a2d35]/40 hover:border-blue-400/60 hover:bg-blue-400/10"
                }`}
              >
                <IconPackage className="h-4 w-4 flex-shrink-0 text-blue-400" />
                <span className="flex-1 truncate text-sm text-zinc-100">
                  {slot.name}
                </span>
                {slot.items.length > 0 && (
                  <span className="flex-shrink-0 rounded-full bg-[#1a1d24]/90 px-1.5 py-0.5 text-[10px] text-zinc-400">
                    {t("warehouseMap.itemCount", { count: slot.items.length })}
                  </span>
                )}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Instructions */}
      <div className="border-t border-[#30333c] p-4">
        <h3 className="mb-2 text-sm font-medium text-zinc-100">{t("warehouseMap.instructions")}</h3>
        <div className="space-y-2 text-xs text-zinc-400">
          <div className="flex items-start gap-2">
            <div className="mt-1 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-blue-400" />
            <span>{t("warehouseMap.slotTemplate.instrDragTemplate")}</span>
          </div>
          <div className="flex items-start gap-2">
            <div className="mt-1 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-blue-400" />
            <span>{t("warehouseMap.slotTemplate.instrClickSlot")}</span>
          </div>
          <div className="flex items-start gap-2">
            <div className="mt-1 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-blue-400" />
            <span>{t("warehouseMap.slotTemplate.instrResize")}</span>
          </div>
          <div className="flex items-start gap-2">
            <div className="mt-1 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-red-400" />
            <span>{t("warehouseMap.slotTemplate.instrDelete")}</span>
          </div>
        </div>
      </div>

      {/* Zone stats */}
      <div className="border-t border-[#30333c] bg-[#2a2d35]/30 p-4">
        <div className="text-xs text-zinc-400">
          {t("warehouseMap.slotTemplate.homeTip")}
        </div>
      </div>

      {renaming && (
        <RenameModal
          title={t("warehouseMap.slotTemplate.renameZoneTitle")}
          label={t("warehouseMap.slotTemplate.zoneNameLabel")}
          initialValue={zoneName}
          enableIcon
          onClose={() => setRenaming(false)}
          onSubmit={(name) => {
            if (name !== zoneName) onRename(name);
            setRenaming(false);
          }}
        />
      )}
    </div>
  );
}
